import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    PhoneOff, MicOff, Mic, User, MessageSquare, Send, X, ChevronDown, Users, Phone,
    Ghost, Mic2, Sparkles, Waves, Moon, Wind, Skull, CircleDashed, Dna, Rocket, Atom, Cpu, Sun, Zap, ArrowLeft, ArrowRight
} from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { doc, onSnapshot, updateDoc, collection, addDoc, getDocs, deleteDoc, setDoc, getDoc, query, orderBy, serverTimestamp, arrayRemove } from 'firebase/firestore';
import sparkLogo from '../assets/spark-logo.png';
import * as Tone from 'tone';
import { useIdentity } from '../context/IdentityContext';
import { useRightPanel } from '../context/RightPanelContext';

// Map icon names to Lucide components (keep consistent with CustomVoiceRoomPage)
const VoiceIcons = {
    Ghost, Mic2, Sparkles, Waves, Moon, Wind, Skull, CircleDashed, Dna, Rocket, Atom, Cpu, Sun, Zap, Mic
};

// Deep STUN/TURN Servers for absolute Symmetric NAT Traversal
const baseIceServers = [
    { urls: [
        'stun:stun.l.google.com:19302', 
        'stun:stun1.l.google.com:19302',
        'stun:stun2.l.google.com:19302',
        'stun:stun3.l.google.com:19302',
        'stun:stun4.l.google.com:19302'
    ]},
    { urls: [
        'stun:global.stun.twilio.com:3478',
        'stun:stun.cloudflare.com:3478',
        'stun:stun.services.mozilla.com'
    ]},
    { urls: "stun:stun.relay.metered.ca:80" }
];

if (process.env.REACT_APP_METERED_TURN_USERNAME && process.env.REACT_APP_METERED_TURN_CREDENTIAL) {
    baseIceServers.push({ urls: "turn:standard.relay.metered.ca:443", username: process.env.REACT_APP_METERED_TURN_USERNAME, credential: process.env.REACT_APP_METERED_TURN_CREDENTIAL });
    baseIceServers.push({ urls: "turns:standard.relay.metered.ca:443?transport=tcp", username: process.env.REACT_APP_METERED_TURN_USERNAME, credential: process.env.REACT_APP_METERED_TURN_CREDENTIAL });
}

const servers = {
    iceServers: baseIceServers,
    iceTransportPolicy: "all"
};

// ==================== VOICE CATEGORIES & PRESETS (match CustomVoiceRoomPage) ====================
const VOICE_CATEGORIES = [
    { name: 'Mysterious', voices: [
        { id: 'phantom', name: 'Phantom', icon: 'Ghost', color: '#BD00FF', description: '(Ghostly voice)', effects: { pitchShift: -3, filterType: 'highpass', filterFreq: 800, distortion: 0, reverbWet: 0.5, chorusWet: 0.3 } },
        { id: 'whisper', name: 'Whisper', icon: 'Mic2', color: '#A9A9A9', description: '(ASMR whisper)', effects: { pitchShift: -1, filterType: 'highpass', filterFreq: 2000, distortion: 0, reverbWet: 0.2, chorusWet: 0 } },
        // { id: 'oracle', name: 'Oracle', icon: 'Sparkles', color: '#7B68EE', description: '(Mystical echo)', effects: { pitchShift: -4, filterType: 'bandpass', filterFreq: 700, distortion: 0.15, reverbWet: 0.7, chorusWet: 0.5 } },
    ]},
    { name: 'Female', voices: [
        { id: 'siren', name: 'Siren', icon: 'Waves', color: '#FF69B4', description: '(Smooth feminine)', effects: { pitchShift: 8, filterType: 'highpass', filterFreq: 1200, distortion: 0, reverbWet: 0.15, chorusWet: 0.25 } },
        { id: 'luna', name: 'Luna', icon: 'Moon', color: '#DDA0DD', description: '(Sweet girl)', effects: { pitchShift: 6, filterType: 'highpass', filterFreq: 900, distortion: 0, reverbWet: 0.1, chorusWet: 0.15 } },
        { id: 'nymph', name: 'Nymph', icon: 'Wind', color: '#E0B0FF', description: '(Anime girl)', effects: { pitchShift: 11, filterType: 'highpass', filterFreq: 1500, distortion: 0, reverbWet: 0.1, chorusWet: 0.35 } },
    ]},
    /* { name: 'Dark', voices: [
        // { id: 'deep-shadow', name: 'Deep Shadow', icon: 'Skull', color: '#0BD9E5', description: '(Deep villain)', effects: { pitchShift: -4, filterType: 'lowpass', filterFreq: 1200, distortion: 0.05, reverbWet: 0.1, chorusWet: 0 } },
        // { id: 'void', name: 'Void', icon: 'CircleDashed', color: '#FF4444', description: '(Ultra-deep)', effects: { pitchShift: -6, filterType: 'lowpass', filterFreq: 1000, distortion: 0.1, reverbWet: 0.2, chorusWet: 0 } },
        // { id: 'beast', name: 'Beast', icon: 'Dna', color: '#8B0000', description: '(Creature growl)', effects: { pitchShift: -7, filterType: 'lowpass', filterFreq: 1200, distortion: 0.4, reverbWet: 0.3, chorusWet: 0 } },
    ]}, */
    /* { name: 'Cosmic', voices: [
        // { id: 'interstellar', name: 'Interstellar', icon: 'Rocket', color: '#BD00FF', description: '(Space explorer)', effects: { pitchShift: 2, filterType: 'highpass', filterFreq: 1200, reverbWet: 0.8, chorusWet: 0.4 } },
        // { id: 'nebula', name: 'Nebula', icon: 'Atom', color: '#00D4FF', description: '(Floating gas)', effects: { pitchShift: -2, filterType: 'bandpass', filterFreq: 1000, reverbWet: 0.6, chorusWet: 0.7 } },
    ]}, */
    { name: 'Digital', voices: [
        { id: 'cyborg', name: 'Cyborg', icon: 'Cpu', color: '#00FF88', description: '(Metallic)', effects: { pitchShift: -2, filterType: 'bandpass', filterFreq: 1200, distortion: 0.55, reverbWet: 0.15, chorusWet: 0.3 } },
        { id: 'ethereal', name: 'Ethereal', icon: 'Sun', color: '#FFD700', description: '(Angelic)', effects: { pitchShift: 5, filterType: 'highpass', filterFreq: 1200, distortion: 0, reverbWet: 0.3, chorusWet: 0.5 } },
    ]},
    { name: 'Fun', voices: [
        { id: 'helium', name: 'Helium', icon: 'Zap', color: '#00CED1', description: '(Chipmunk)', effects: { pitchShift: 12, filterType: 'highpass', filterFreq: 1600, distortion: 0, reverbWet: 0, chorusWet: 0.1 } },
        { id: 'original', name: 'Original', icon: 'Mic', color: '#FFFFFF', description: '(Real voice)', effects: null },
    ]},
];

const ALL_VOICE_PRESETS = VOICE_CATEGORIES.flatMap(cat => cat.voices);

// Random assignment pool: skip Dark + skip Original
const RANDOM_ASSIGNMENT_POOL = ALL_VOICE_PRESETS.filter(v => {
    const category = VOICE_CATEGORIES.find(cat => cat.voices.some(voice => voice.id === v.id));
    return v.effects !== null &&
        v.id !== 'original' &&
        category &&
        category.name !== 'Dark';
});

function getCryptoRandomIndex(maxExclusive) {
    if (maxExclusive <= 1) return 0;
    try {
        const arr = new Uint32Array(1);
        window.crypto.getRandomValues(arr);
        return arr[0] % maxExclusive;
    } catch {
        return Math.floor(Math.random() * maxExclusive);
    }
}

function getRandomVoice(storageKey = 'spark:lastVoiceIds:group') {
    if (RANDOM_ASSIGNMENT_POOL.length === 0) return ALL_VOICE_PRESETS.find(v => v.id === 'original') || ALL_VOICE_PRESETS[0];
    if (RANDOM_ASSIGNMENT_POOL.length === 1) return RANDOM_ASSIGNMENT_POOL[0];

    let recent = [];
    try { recent = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch {}
    if (!Array.isArray(recent)) recent = [];
    recent = recent.filter(Boolean).slice(-3);

    const candidates = RANDOM_ASSIGNMENT_POOL.filter(v => !recent.includes(v.id));
    const pool = candidates.length > 0 ? candidates : RANDOM_ASSIGNMENT_POOL;
    const picked = pool[getCryptoRandomIndex(pool.length)];

    try {
        const nextRecent = [...recent, picked.id].slice(-3);
        localStorage.setItem(storageKey, JSON.stringify(nextRecent));
    } catch {}

    return picked;
}

// ==================== TONE.JS AUDIO PROCESSING ====================
function buildToneChain(rawStream, preset, toneNodesRef) {
    cleanupToneChain(toneNodesRef);
    if (!preset.effects) return rawStream;

    const fx = preset.effects;
    const ctx = Tone.getContext();
    const source = ctx.createMediaStreamSource(rawStream);
    const nodes = [];

    const pitchShift = new Tone.PitchShift({ pitch: fx.pitchShift, windowSize: 0.1 });
    nodes.push(pitchShift);
    const filter = new Tone.Filter({ type: fx.filterType, frequency: fx.filterFreq, rolloff: -24 });
    nodes.push(filter);
    if (fx.distortion > 0) { nodes.push(new Tone.Distortion(fx.distortion)); }
    if (fx.chorusWet > 0) { nodes.push(new Tone.Chorus({ frequency: 2, delayTime: 3, depth: 0.7, wet: fx.chorusWet }).start()); }
    if (fx.reverbWet > 0) { nodes.push(new Tone.Reverb({ decay: 1.5, wet: fx.reverbWet })); }

    const destination = ctx.rawContext.createMediaStreamDestination();
    const toneDestGain = new Tone.Gain(1);
    nodes.push(toneDestGain);

    const sourceGain = ctx.rawContext.createGain();
    sourceGain.gain.value = 1.0;
    source.connect(sourceGain);
    const inputGain = new Tone.Gain(1);
    Tone.connect(sourceGain, inputGain);

    let chain = inputGain;
    for (const node of nodes) { chain.connect(node); chain = node; }
    Tone.connect(chain, destination);

    toneNodesRef.current = { source, sourceGain, inputGain, nodes, destination };
    return destination.stream;
}

function cleanupToneChain(toneNodesRef) {
    if (toneNodesRef.current) {
        const { source, sourceGain, inputGain, nodes } = toneNodesRef.current;
        try { source?.disconnect(); sourceGain?.disconnect(); inputGain?.dispose(); nodes?.forEach(n => { try { n.dispose(); } catch(e){} }); } catch(e) {}
        toneNodesRef.current = null;
    }
}

// ==================== COMPONENT ====================
export const GroupVoiceRoomPage = () => {
    const { identityMode } = useIdentity();
    const { setIsRightPanelOpen } = useRightPanel();
    const { roomId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const initialRoomData = location.state?.roomData;

    const [roomData, setRoomData] = useState(initialRoomData || null);
    const [isMuted, setIsMuted] = useState(false);
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [callActive, setCallActive] = useState(false);

    // Voice Effect State
    const [activeVoice, setActiveVoice] = useState(getRandomVoice());
    const [showVoicePicker, setShowVoicePicker] = useState(false);
    
    // Chat State
    const isDesktopDevice = typeof window !== 'undefined' && window.innerWidth >= 640;
    const [showChat, setShowChat] = useState(isDesktopDevice);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const chatEndRef = useRef(null);
    const showChatRef = useRef(isDesktopDevice);

    const localStreamRef = useRef(null);
    const processedStreamRef = useRef(null);
    const toneNodesRef = useRef(null);
    
    // Mesh WebRTC: one RTCPeerConnection per remote peer
    const peerConnectionsRef = useRef({}); // { [remoteUid]: RTCPeerConnection }
    const remoteAudioRefs = useRef({});    // { [remoteUid]: HTMLAudioElement }
    const [connectedPeers, setConnectedPeers] = useState(new Set());
    const [speakingPeers, setSpeakingPeers] = useState(new Set()); // who is currently speaking
    const audioAnalysersRef = useRef({}); // { [uid]: { analyser, dataArray } }
    const localAnalyserRef = useRef(null);
    
    const myUid = auth.currentUser?.uid;
    const hasLeftRef = useRef(false);

    // ==================== ROOM LISTENER ====================
    useEffect(() => {
        if (!roomId) return;
        let cancelled = false;

        const roomDocRef = doc(db, 'groupVoiceRooms', roomId);
        const unsubRoom = onSnapshot(roomDocRef, (docSnap) => {
            if (cancelled) return;
            if (!docSnap.exists() || docSnap.data().ended) {
                handleLeave(true);
                return;
            }
            const data = docSnap.data();
            setRoomData(data);
            
            // Check if I was removed
            if (!data.participantUids?.includes(myUid)) {
                handleLeave(true);
                return;
            }

            // If we have 2+ participants, call is active
            if (data.participantUids?.length >= 2) {
                setCallActive(true);
            }
        });

        return () => { cancelled = true; unsubRoom(); };
    }, [roomId]);

    // ==================== MESH WEBRTC ====================
    useEffect(() => {
        if (!roomId || !roomData || !myUid) return;
        let cancelled = false;

        const initMesh = async () => {
            try {
                // Log successful connection to a group room
                logEvent(analytics, 'room_connected', { type: '4-4_voice' });
                
                await Tone.start();
                
                // Get mic if not already
                if (!localStreamRef.current) {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                    if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
                    localStreamRef.current = stream;
                    
                    const processed = buildToneChain(stream, activeVoice, toneNodesRef);
                    processedStreamRef.current = processed;
                    
                    // Sync voice to Firestore
                    updateDoc(doc(db, 'groupVoiceRooms', roomId), {
                        [`participantInfo.${myUid}.voiceEffect`]: { name: activeVoice.name, icon: activeVoice.icon, color: activeVoice.color }
                    }).catch(() => {});
                    
                    // Set up local audio analyser for self-speaking detection
                    try {
                        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                        const src = audioCtx.createMediaStreamSource(stream);
                        const analyser = audioCtx.createAnalyser();
                        analyser.fftSize = 256;
                        analyser.smoothingTimeConstant = 0.5;
                        src.connect(analyser);
                        const dataArray = new Uint8Array(analyser.frequencyBinCount);
                        localAnalyserRef.current = { analyser, dataArray, audioCtx };
                    } catch(e) {}
                }
            } catch (e) {
                console.error("Mic error:", e);
            }
        };

        initMesh();

        // Listen for signaling
        const signalsRef = collection(db, 'groupVoiceRooms', roomId, 'signals');
        const signalQuery = query(signalsRef, orderBy('timestamp', 'asc'));
        
        const unsubSignals = onSnapshot(signalQuery, async (snapshot) => {
            if (cancelled) return;
            
            for (const change of snapshot.docChanges()) {
                if (change.type !== 'added') continue;
                const signal = change.doc.data();
                
                // Only process signals addressed to me
                if (signal.to !== myUid) continue;
                
                const fromUid = signal.from;
                
                if (signal.type === 'offer') {
                    // Someone sent us an offer — create answer
                    const pc = getOrCreatePC(fromUid);
                    await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    
                    await addDoc(signalsRef, {
                        from: myUid,
                        to: fromUid,
                        type: 'answer',
                        data: { type: answer.type, sdp: answer.sdp },
                        timestamp: serverTimestamp()
                    });
                    
                } else if (signal.type === 'answer') {
                    const pc = peerConnectionsRef.current[fromUid];
                    if (pc && !pc.currentRemoteDescription) {
                        await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
                    }
                    
                } else if (signal.type === 'candidate') {
                    const pc = peerConnectionsRef.current[fromUid];
                    if (pc) {
                        try { await pc.addIceCandidate(new RTCIceCandidate(signal.data)); } catch(e) {}
                    }
                }
            }
        });

        return () => { cancelled = true; unsubSignals(); };
    }, [roomId, myUid]);

    // Watch for new participants joining — initiate WebRTC to them
    useEffect(() => {
        if (!roomData || !myUid || !processedStreamRef.current) return;
        
        const participants = roomData.participantUids || [];
        const otherParticipants = participants.filter(uid => uid !== myUid);
        
        for (const remoteUid of otherParticipants) {
            if (!peerConnectionsRef.current[remoteUid]) {
                // I should initiate if my UID is "greater" (deterministic who offers)
                if (myUid > remoteUid) {
                    initiateConnectionTo(remoteUid);
                }
                // Otherwise, wait for their offer via signals
            }
        }
        
        // Clean up connections for participants who left
        for (const uid of Object.keys(peerConnectionsRef.current)) {
            if (!participants.includes(uid)) {
                console.log("Peer left, cleaning up:", uid);
                peerConnectionsRef.current[uid]?.close();
                delete peerConnectionsRef.current[uid];
                if (remoteAudioRefs.current[uid]) {
                    remoteAudioRefs.current[uid].srcObject = null;
                    delete remoteAudioRefs.current[uid];
                }
                setConnectedPeers(prev => { const n = new Set(prev); n.delete(uid); return n; });
            }
        }
    }, [roomData?.participantUids, myUid]);

    // Helper: create or get a PeerConnection to a remote user
    const getOrCreatePC = useCallback((remoteUid) => {
        if (peerConnectionsRef.current[remoteUid]) return peerConnectionsRef.current[remoteUid];
        
        const pc = new RTCPeerConnection(servers);
        peerConnectionsRef.current[remoteUid] = pc;
        
        // Add local processed audio tracks
        if (processedStreamRef.current) {
            processedStreamRef.current.getTracks().forEach(track => {
                pc.addTrack(track, processedStreamRef.current);
            });
        }
        
        // Handle remote audio
        const remoteStream = new MediaStream();
        pc.ontrack = (event) => {
            event.streams[0].getTracks().forEach(track => remoteStream.addTrack(track));
            // Create or update audio element for this peer
            if (!remoteAudioRefs.current[remoteUid]) {
                const audio = new Audio();
                audio.autoplay = true;
                audio.playsInline = true;
                remoteAudioRefs.current[remoteUid] = audio;
            }
            remoteAudioRefs.current[remoteUid].srcObject = remoteStream;
            
            // Set up audio analyser for speaking detection
            try {
                const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                const source = audioCtx.createMediaStreamSource(remoteStream);
                const analyser = audioCtx.createAnalyser();
                analyser.fftSize = 256;
                analyser.smoothingTimeConstant = 0.5;
                source.connect(analyser);
                const dataArray = new Uint8Array(analyser.frequencyBinCount);
                audioAnalysersRef.current[remoteUid] = { analyser, dataArray, audioCtx };
            } catch(e) { console.error("Analyser setup error:", e); }
        };
        
        // Connection state
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected') {
                setConnectedPeers(prev => new Set([...prev, remoteUid]));
            } else if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
                setConnectedPeers(prev => { const n = new Set(prev); n.delete(remoteUid); return n; });
            }
        };
        
        // ICE candidates
        const signalsRef = collection(db, 'groupVoiceRooms', roomId, 'signals');
        pc.onicecandidate = (event) => {
            if (event.candidate) {
                addDoc(signalsRef, {
                    from: myUid,
                    to: remoteUid,
                    type: 'candidate',
                    data: event.candidate.toJSON(),
                    timestamp: serverTimestamp()
                }).catch(() => {});
            }
        };
        
        return pc;
    }, [roomId, myUid]);

    // Initiate WebRTC connection to a specific peer
    const initiateConnectionTo = useCallback(async (remoteUid) => {
        console.log("Initiating WebRTC to:", remoteUid);
        const pc = getOrCreatePC(remoteUid);
        
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        
        const signalsRef = collection(db, 'groupVoiceRooms', roomId, 'signals');
        await addDoc(signalsRef, {
            from: myUid,
            to: remoteUid,
            type: 'offer',
            data: { type: offer.type, sdp: offer.sdp },
            timestamp: serverTimestamp()
        });
    }, [roomId, myUid, getOrCreatePC]);

    // ==================== CHAT LISTENER ====================
    useEffect(() => {
        if (!roomId) return;
        const messagesRef = collection(db, 'groupVoiceRooms', roomId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        let isFirstSnapshot = true;
        
        const unsub = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setChatMessages(msgs);
            if (!isFirstSnapshot && !showChatRef.current) {
                const newMsgs = snapshot.docChanges().filter(c => c.type === 'added' && c.doc.data().sender !== myUid);
                if (newMsgs.length > 0) setUnreadCount(prev => prev + newMsgs.length);
            }
            isFirstSnapshot = false;
        });
        return () => unsub();
    }, [roomId]);

    // Auto-scroll chat
    useEffect(() => {
        if (showChat && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages, showChat]);

    // Timer
    useEffect(() => {
        let interval;
        if (callActive) interval = setInterval(() => setTimerSeconds(prev => prev + 1), 1000);
        return () => clearInterval(interval);
    }, [callActive]);

    // Speaking detection loop (polls every 150ms)
    useEffect(() => {
        if (!callActive) return;
        const THRESHOLD = 25; // audio level threshold for "speaking"
        
        const interval = setInterval(() => {
            const speaking = new Set();
            
            // Check remote peers
            for (const [uid, analyserData] of Object.entries(audioAnalysersRef.current)) {
                try {
                    const { analyser, dataArray } = analyserData;
                    analyser.getByteFrequencyData(dataArray);
                    const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                    if (avg > THRESHOLD) speaking.add(uid);
                } catch(e) {}
            }
            
            // Check local (self)
            if (localAnalyserRef.current && !isMuted) {
                try {
                    const { analyser, dataArray } = localAnalyserRef.current;
                    analyser.getByteFrequencyData(dataArray);
                    const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
                    if (avg > THRESHOLD) speaking.add('self');
                } catch(e) {}
            }
            
            setSpeakingPeers(speaking);
        }, 150);
        
        return () => clearInterval(interval);
    }, [callActive, isMuted]);

    // ==================== VOICE SWITCHING ====================
    const switchVoice = (preset) => {
        setActiveVoice(preset);
        setShowVoicePicker(false);

        if (!localStreamRef.current) return;
        const rawStream = localStreamRef.current;
        const newProcessed = buildToneChain(rawStream, preset, toneNodesRef);
        processedStreamRef.current = newProcessed;

        // Replace tracks on all peer connections
        for (const [uid, pc] of Object.entries(peerConnectionsRef.current)) {
            const audioSender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
            if (audioSender && newProcessed.getAudioTracks().length > 0) {
                audioSender.replaceTrack(newProcessed.getAudioTracks()[0]);
            }
        }

        // Sync to Firestore
        if (roomId) {
            updateDoc(doc(db, 'groupVoiceRooms', roomId), {
                [`participantInfo.${myUid}.voiceEffect`]: { name: preset.name, icon: preset.icon, color: preset.color }
            }).catch(() => {});
        }
    };

    // ==================== HANDLERS ====================
    const handleLeave = async (isNext = false) => {
        if (hasLeftRef.current) return;
        hasLeftRef.current = true;
        
        // Close all peer connections
        for (const pc of Object.values(peerConnectionsRef.current)) {
            try { pc.close(); } catch(e) {}
        }
        peerConnectionsRef.current = {};
        
        // Stop audio elements
        for (const audio of Object.values(remoteAudioRefs.current)) {
            try { audio.srcObject = null; } catch(e) {}
        }
        remoteAudioRefs.current = {};
        
        // Clean up audio analysers
        for (const data of Object.values(audioAnalysersRef.current)) {
            try { data.audioCtx?.close(); } catch(e) {}
        }
        audioAnalysersRef.current = {};
        if (localAnalyserRef.current) {
            try { localAnalyserRef.current.audioCtx?.close(); } catch(e) {}
            localAnalyserRef.current = null;
        }
        
        // Cleanup Tone.js
        cleanupToneChain(toneNodesRef);
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach(t => t.stop());
            localStreamRef.current = null;
        }
        
        // Remove self from room
        if (roomId && myUid) {
            try {
                const roomRef = doc(db, 'groupVoiceRooms', roomId);
                const roomSnap = await getDoc(roomRef);
                if (roomSnap.exists()) {
                    const data = roomSnap.data();
                    const remaining = (data.participantUids || []).filter(u => u !== myUid);
                    
                    if (remaining.length === 0) {
                        // Last person — mark ended
                        await updateDoc(roomRef, { ended: true, participantUids: [] });
                    } else {
                        // Remove self
                        await updateDoc(roomRef, {
                            participantUids: arrayRemove(myUid),
                            [`participantInfo.${myUid}`]: null
                        });
                    }
                }
            } catch(e) { console.error("Leave error:", e); }
        }
        
        navigate('/group-voice-setup', { replace: true, state: { autoSearch: true, selectedMode: roomData?.mode || 'global' } });
    };

    const toggleMute = () => {
        const newMuted = !isMuted;
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = !newMuted; });
        }
        // Mute on all senders
        for (const pc of Object.values(peerConnectionsRef.current)) {
            pc.getSenders().forEach(sender => {
                if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !newMuted;
            });
        }
        setIsMuted(newMuted);
        
        if (roomId && myUid) {
            updateDoc(doc(db, 'groupVoiceRooms', roomId), {
                [`participantInfo.${myUid}.muted`]: newMuted
            }).catch(() => {});
        }
    };
    // Right Panel Sync
    useEffect(() => {
        setIsRightPanelOpen(showChat);
        return () => setIsRightPanelOpen(false);
    }, [showChat, setIsRightPanelOpen]);

    const sendChatMessage = async () => {
        if (!chatInput.trim() || !roomId) return;
        const text = chatInput.trim();
        setChatInput('');
        try {
            const myInfo = roomData?.participantInfo?.[myUid];
            await addDoc(collection(db, 'groupVoiceRooms', roomId, 'messages'), {
                sender: myUid, senderAlias: myInfo?.alias || 'Anonymous', text, timestamp: serverTimestamp()
            });
        } catch (e) { console.error('Chat send error:', e); }
    };

    const handleChatKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } };
    const toggleChat = () => { 
        setShowChat(prev => {
            const next = !prev;
            showChatRef.current = next;
            if (next) setUnreadCount(0);
            return next;
        }); 
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    // ==================== DERIVED DATA ====================
    const participants = roomData?.participantUids || [];
    const participantInfo = roomData?.participantInfo || {};
    const myInfo = participantInfo[myUid];
    const otherParticipants = participants.filter(uid => uid !== myUid);
    
    // Build grid slots (always 4, fill empty with null)
    const gridSlots = [];
    for (const uid of otherParticipants) {
        gridSlots.push({ uid, info: participantInfo[uid] });
    }
    while (gridSlots.length < 3) {
        gridSlots.push(null); // empty slot
    }

    // ==================== RENDER ====================
    if (!roomData) return <div className="h-screen bg-black flex items-center justify-center"><div className="w-8 h-8 border-t-2 border-b-2 border-[#FF6600] rounded-full animate-spin" /></div>;

    if (identityMode === 'real') {
       return (
         <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
            <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
               <Users size={32} />
            </div>
            <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">4-4 Room</h2>
            <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
         </main>
       );
    }

    return (
        <main className="w-full sm:w-[700px] h-screen sm:h-auto sm:min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
            
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none z-0">
                <div className="absolute inset-0 transition-opacity duration-1000 opacity-60"
                     style={{ background: 'radial-gradient(circle at 50% 30%, rgba(255,102,0,0.12), transparent 70%)' }} />
            </div>

            <div className="relative z-10 flex flex-col h-full">

                {/* Header */}
                  <div className="flex items-center justify-between pt-3 px-4 pb-1 relative">
                      <div className="flex items-center">
                          {/* sparkLogo removed */}
                      </div>
                    
                    <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
                        <Users size={16} className="text-[#FF6600]" />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#FF6600]">Group Voice</span>
                    </div>

                    <div className="flex items-center gap-1.5 text-gray-400">
                        <span className="text-xs font-mono">{participants.length}/4</span>
                    </div>
                </div>

                {/* 2x2 Full-Screen Grid */}
                <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-1.5 px-2 py-1">
                    {gridSlots.map((slot, i) => {
                        const isSpeaking = slot ? speakingPeers.has(slot.uid) : false;
                        return (
                            <div key={slot?.uid || `empty-${i}`} 
                                 className="rounded-2xl bg-gray-900/60 backdrop-blur-sm flex flex-col items-center justify-center gap-2 relative overflow-hidden transition-all duration-200"
                                 style={{ 
                                     border: isSpeaking ? '2px solid #FF6600' : '1px solid rgba(55,65,81,0.5)',
                                     boxShadow: isSpeaking ? '0 0 25px rgba(255,102,0,0.3), inset 0 0 25px rgba(255,102,0,0.05)' : 'none'
                                 }}>
                                {slot ? (
                                    <>
                                        <div className="relative">
                                            {/* Speaking pulse rings */}
                                            {isSpeaking && (
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                    <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }} transition={{ duration: 1.2, repeat: Infinity }} className="absolute w-20 h-20 border-2 border-[#FF6600] rounded-full" />
                                                </div>
                                            )}
                                            <div className={`w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden shadow-xl transition-all duration-200 ${isSpeaking ? 'border-[3px] border-[#FF6600] scale-105' : 'border-2 border-gray-600'}`}>
                                                {slot.info?.alias ? (
                                                    <img src={`/avatars/${slot.info.alias}.png`} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <User className="text-gray-500 w-8 h-8" />
                                                )}
                                            </div>
                                            {slot.info?.muted && (
                                                <div className="absolute -top-0.5 -right-0.5 z-20 w-6 h-6 rounded-full bg-red-500/90 flex items-center justify-center">
                                                    <MicOff size={10} className="text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-base font-bold truncate max-w-full px-3">{slot.info?.alias || 'Anonymous'}</p>
                                        <p className="text-[10px] text-gray-500 uppercase tracking-wider truncate max-w-full px-3">{slot.info?.college || ''}</p>
                                        {slot.info?.voiceEffect && (
                                            <div className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-black/50 border border-gray-700">
                                                {(() => {
                                                    const Icon = VoiceIcons[slot.info.voiceEffect.icon] || User;
                                                    return <Icon size={11} style={{ color: slot.info.voiceEffect.color }} />;
                                                })()}
                                                <span className="text-[8px] font-bold uppercase" style={{ color: slot.info.voiceEffect.color }}>{slot.info.voiceEffect.name}</span>
                                            </div>
                                        )}
                                        <div className={`absolute top-3 left-3 w-2.5 h-2.5 rounded-full ${connectedPeers.has(slot.uid) ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
                                    </>
                                ) : (
                                    <>
                                        <div className="w-20 h-20 rounded-full border-2 border-dashed border-gray-700 flex items-center justify-center">
                                            <User className="text-gray-700 w-8 h-8" />
                                        </div>
                                        <p className="text-sm text-gray-600 font-medium">Waiting...</p>
                                        <motion.div 
                                            animate={{ opacity: [0.3, 0.6, 0.3] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                            className="w-16 h-0.5 bg-gray-700 rounded-full"
                                        />
                                    </>
                                )}
                            </div>
                        );
                    })}
                    
                    {/* My own card */}
                    <div className="rounded-2xl bg-gray-900/60 backdrop-blur-sm flex flex-col items-center justify-center gap-2 relative overflow-hidden transition-all duration-200"
                         style={{
                             border: speakingPeers.has('self') ? '2px solid #FF6600' : '1px solid rgba(255,102,0,0.2)',
                             boxShadow: speakingPeers.has('self') ? '0 0 25px rgba(255,102,0,0.3), inset 0 0 25px rgba(255,102,0,0.05)' : 'none',
                             background: 'rgba(255,102,0,0.03)'
                         }}>
                        <div className="relative mb-1 mt-2">
                            {/* Speaking pulse rings */}
                            {speakingPeers.has('self') && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }} transition={{ duration: 1.2, repeat: Infinity }} className="absolute w-20 h-20 border-2 border-[#FF6600] rounded-full" />
                                </div>
                            )}
                            <div className={`w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden shadow-xl transition-all duration-200 ${speakingPeers.has('self') ? 'border-[3px] border-[#FF6600] scale-105' : 'border-2 border-[#FF6600]/40'}`}>
                                {myInfo?.alias ? (
                                    <img src={`/avatars/${myInfo.alias}.png`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <User className="text-gray-500 w-8 h-8" />
                                )}
                            </div>
                            {isMuted && (
                                <div className="absolute -top-0.5 -right-0.5 z-20 w-6 h-6 rounded-full bg-red-500/90 flex items-center justify-center">
                                    <MicOff size={10} className="text-white" />
                                </div>
                            )}
                        </div>
                        <p className="text-base font-bold truncate max-w-full px-3">{myInfo?.alias || 'You'} <span className="text-[10px] text-gray-500">(YOU)</span></p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider truncate max-w-full px-3">{myInfo?.college || ''}</p>
                        {callActive && (
                            <div className="absolute top-3 left-3 w-2.5 h-2.5 rounded-full bg-green-500" />
                        )}
                    </div>
                </div>

                {/* Timer overlay */}
                {callActive && (
                    <div className="absolute top-14 left-1/2 transform -translate-x-1/2 flex items-center gap-2 px-3 py-1 rounded-full bg-black/70 backdrop-blur-sm border border-gray-800 z-20">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <p className="font-mono text-gray-300 font-bold text-sm">{formatTime(timerSeconds)}</p>
                    </div>
                )}

                {/* Call Controls */}
                <div className="pb-10 pt-4 flex items-center justify-center gap-6 bg-gradient-to-t from-black via-black to-transparent">
                    <button 
                        onClick={toggleMute}
                        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${isMuted ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
                    >
                        {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
                    </button>

                    <button 
                        onClick={toggleChat}
                        className="w-14 h-14 rounded-full bg-gray-800 text-white hover:bg-gray-700 flex items-center justify-center transition-colors shadow-lg relative"
                    >
                        <MessageSquare size={24} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </button>

                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => handleLeave(true)}
                            className="w-20 h-20 rounded-[2.5rem] bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-transform hover:scale-105 shadow-[0_0_30px_rgba(239,68,68,0.3)]"
                        >
                            <Phone size={32} className="rotate-[135deg]" />
                        </button>

                        <button 
                            className="w-14 h-14 rounded-full border-2 bg-gray-800 flex items-center justify-center transition-all shadow-lg hover:bg-gray-700"
                            style={{ borderColor: activeVoice.color + '80' }}
                            onClick={() => setShowVoicePicker(!showVoicePicker)}
                            title={`Change Voice: ${activeVoice.name}`}
                        >
                            {(() => {
                                const Icon = VoiceIcons[activeVoice.icon] || User;
                                return <Icon size={24} style={{ color: activeVoice.color, filter: `drop-shadow(0 0 8px ${activeVoice.color}40)` }} />;
                            })()}
                        </button>
                    </div>
                </div>
            </div>

            {/* ==================== VOICE PRESET PICKER ==================== */}
            <AnimatePresence>
                {showVoicePicker && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="absolute bottom-0 left-0 right-0 z-50 bg-[#111] border-t border-gray-800 rounded-t-3xl p-6 pb-10"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold">Voice Effect</h3>
                            <button onClick={() => setShowVoicePicker(false)} className="text-gray-500 hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
                            {VOICE_CATEGORIES.map((category) => (
                                <div key={category.name}>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2 pl-1">{category.name}</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {category.voices.map((preset) => (
                                            <div
                                                key={preset.id}
                                                className={`flex flex-col items-center gap-1 p-2.5 rounded-2xl border transition-all cursor-pointer ${
                                                    activeVoice.id === preset.id 
                                                        ? 'scale-[1.03]' 
                                                        : 'border-gray-800 bg-transparent hover:bg-white/5'
                                                }`}
                                                style={{
                                                    borderColor: activeVoice.id === preset.id ? preset.color : undefined,
                                                    backgroundColor: activeVoice.id === preset.id ? preset.color + '15' : undefined,
                                                    boxShadow: activeVoice.id === preset.id ? `0 0 20px ${preset.color}30` : undefined
                                                }}
                                                onClick={() => switchVoice(preset)}
                                            >
                                                {(() => {
                                                    const Icon = VoiceIcons[preset.icon] || User;
                                                    return <Icon size={18} style={{ color: preset.color, filter: `drop-shadow(0 0 8px ${preset.color}40)` }} />;
                                                })()}
                                                <span className="text-[9px] font-bold uppercase tracking-wider leading-tight" style={{ color: activeVoice.id === preset.id ? preset.color : '#9CA3AF' }}>
                                                    {preset.name}
                                                </span>
                                                <span className="text-[7px] text-gray-500 leading-tight text-center">
                                                    {preset.description}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ==================== IN-CALL CHAT PANEL ==================== */}
            {(() => {
                const chatPanelContent = (
                    <div className="flex flex-col h-full bg-black/95 backdrop-blur-xl lg:bg-black w-full border-gray-800 lg:border-l-0">
                        {/* Chat Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-800 lg:hidden">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                                    <MessageSquare size={16} className="text-[#0BD9E5]" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold">Room Chat</p>
                                    <p className="text-[10px] text-[#0BD9E5] font-medium">{formatTime(timerSeconds)} • {participants.length + 1} Active</p>
                                </div>
                            </div>
                            <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-white transition-colors p-2">
                                <X size={20} />
                            </button>
                        </div>
                        
                        {/* Desktop Chat Header */}
                        <div className="hidden lg:flex items-center justify-between p-4 border-b border-gray-800">
                             <h3 className="font-extrabold text-[20px]">Room Chat</h3>
                             <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-white transition-colors p-2">
                                 <X size={20} />
                             </button>
                        </div>

                        {/* Chat Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {chatMessages.length === 0 && (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
                                    <MessageSquare size={32} className="mb-3 opacity-30" />
                                    <p>Say hi to the group!</p>
                                </div>
                            )}
                            {chatMessages.map((msg) => {
                                const isMe = msg.sender === myUid;
                                const participant = participantInfo[msg.sender];
                                const alias = isMe ? 'You' : (participant?.alias || 'Anonymous');

                                return (
                                    <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`flex items-end gap-2 max-w-[85%] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                            {/* Avatar (For incoming only) */}
                                            {!isMe && (
                                                <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0 flex items-center justify-center overflow-hidden mb-1">
                                                    <img src={`/avatars/${alias}.png`} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                                    <User size={12} className="text-gray-500 absolute" style={{ zIndex: -1 }} />
                                                </div>
                                            )}

                                            {/* Message Content */}
                                            <div className="flex flex-col">
                                                {!isMe && (
                                                    <p className="text-[10px] text-gray-500 font-bold mb-1 ml-2">{alias}</p>
                                                )}
                                                <div className={`px-4 py-2.5 rounded-2xl text-sm break-words ${
                                                    isMe 
                                                        ? 'bg-[#0BD9E5] text-black rounded-br-sm' 
                                                        : 'bg-gray-800 text-white rounded-bl-sm'
                                                }`}>
                                                    {msg.text}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Chat Input */}
                        <div className="p-4 border-t border-gray-800">
                            <div className="flex items-center gap-3 bg-gray-900 rounded-2xl px-4 py-2 border border-gray-800 focus-within:border-[#0BD9E5]/50 transition-colors">
                                <input
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={handleChatKeyDown}
                                    placeholder="Type to the group..."
                                    className="flex-1 bg-transparent text-white text-sm outline-none placeholder-gray-500"
                                    autoFocus
                                />
                                <button 
                                    onClick={sendChatMessage}
                                    disabled={!chatInput.trim()}
                                    className="text-[#0BD9E5] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <Send size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                );

                const portalTarget = document.getElementById('right-panel-chat-container');

                return (
                    <>
                        {/* Mobile Overlay */}
                        <div className="lg:hidden">
                            <AnimatePresence>
                                {showChat && (
                                    <motion.div
                                        initial={{ y: '100%' }}
                                        animate={{ y: 0 }}
                                        exit={{ y: '100%' }}
                                        transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
                                        className="absolute inset-0 z-50 flex flex-col"
                                    >
                                        {chatPanelContent}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        {/* Desktop Portal */}
                        {showChat && portalTarget && ReactDOM.createPortal(chatPanelContent, portalTarget)}
                    </>
                );
            })()}

        </main>
    );
};

export default GroupVoiceRoomPage;
